import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { readFiles } from '@shared/fileReaderUtils';
import { IBase } from '@shared/models/api/base';
import { catchError, defer, map, mergeMap, Observable, of, throwError } from 'rxjs';
import { IBalance, IConfiguration, ILayerParam, IPalletType, IPalletTypeCombination, IPool, ISort, IStackParam, TBalanceMethod } from '../../../masterdata/palletising/configuration/types';
import { CrudService } from '../crud.service';
import { ExcelFileTransfer, xlsxImportError } from '../file-transfer-services/excelFileTransfer.service';
import { IFileDataPropertySchema, IFileDataSchema, IFileDataSchemaProperties, IImportedData } from '../file-transfer-services/interfaces';
import { PalletisingService } from '../palletising.service';
import { FailedImport, FailedParse, FailedValidationImport, ImportErrorTypes, ImportResponse, ImportStatus, ITransferService, SuccessFullImport } from './interfaces';

const fileName = 'Palletising-configuration';
const configId = 'configuration';

const CONFIGURATION_SHEET = 'Configuration';
const STACK_PARAM_SHEET = 'StackParams';
const LAYER_PARAM_SHEET = 'LayerParams';
const COMBINATION_SHEET = 'PalletTypeCombinations';

const ORDER_LABELS = ['Ascending', 'Descending'] as const;
type OrderLabel = (typeof ORDER_LABELS)[number];

const BALANCE_LABELS = ['None', 'Uniform', 'Edge', 'Center'] as const;
type BalanceLabel = (typeof BALANCE_LABELS)[number];

const STACK_TYPE_SELF = '/palletising/configuration-stacking-rule-types/height';
const LAYER_TYPE_SELF = '/palletising/configuration-stacking-rule-types/layer';

const SCALAR_RANGE_MAX = 2_147_483_647;
const PRIORITY_MAX = 2_147_483_647;
const STACKING_VALUE_MIN = 1;
const STACKING_VALUE_MAX = 10_000;

export interface IConfigurationExportData {
	palletMaxRows: number;
	palletMaxLayers: number;
	palletMaxHeight: number;
	palletElementPadding: number;
	rotateMaxWeight: number;
	rotateMaxLengthToHeightAspectRatio: number;
	rotateMinRatioSingleElementOnly: boolean;
	allowStackingWithoutSupportingBase: boolean;
	balancingMethod: string;

	sortElementLengthEnabled: boolean;
	sortElementLengthOrder?: string;
	sortElementLengthPriority?: number;
	sortPositionWeightEnabled: boolean;
	sortPositionWeightOrder?: string;
	sortPositionWeightPriority?: number;
	sortBatchNumberEnabled: boolean;
	sortBatchNumberOrder?: string;
	sortBatchNumberPriority?: number;
	sortProductionSequenceNumberEnabled: boolean;
	sortProductionSequenceNumberOrder?: string;
	sortProductionSequenceNumberPriority?: number;

	poolPalletEnabled: boolean;
	poolPalletUnspecified?: boolean;
	poolPalletPriority?: number;
	poolBatchEnabled: boolean;
	poolBatchUnspecified?: boolean;
	poolBatchPriority?: number;
	poolOrderEnabled: boolean;
	poolOrderUnspecified?: boolean;
	poolOrderPriority?: number;
	poolPartitionEnabled: boolean;
	poolPartitionUnspecified?: boolean;
	poolPartitionPriority?: number;
	poolPalletDimensionEnabled: boolean;
	poolPalletDimensionUnspecified?: boolean;
	poolPalletDimensionPriority?: number;
	poolPalletTypeEnabled: boolean;
	poolPalletTypeUnspecified?: boolean;
	poolPalletTypePriority?: number;
	poolGlassFittingEnabled: boolean;
	poolGlassFittingUnspecified?: boolean;
	poolGlassFittingPriority?: number;
}

export interface IStackParamExportData {
	toHeight: number;
	maxHeightAfter: number;
	maxWeightElement: number;
}

export interface ILayerParamExportData {
	layer: number;
	maxHeightAfter: number;
	maxWeightElement: number;
}

export interface ICombinationExportData {
	type: string;
	elementType: string;
}

const HEADER_WIDTH_PADDING = 2;

/** Every column at least as wide as its own header text, so a hand-picked width can't drift out of sync the moment a header is renamed. Mirrors carting-config.ts's own helper (duplicated rather than shared, since it is a small pure function and the two entities' schemas are otherwise unrelated). */
function withHeaderWidths<T>(schema: IFileDataSchema<T>): IFileDataSchema<T> {
	const properties = Object.fromEntries(
		Object.entries(schema.properties).map(([key, property]) => {
			const prop = property as IFileDataPropertySchema<unknown>;
			const headerText = prop.fileKey ?? key;
			return [key, { ...prop, width: Math.max(prop.width ?? 0, headerText.length + HEADER_WIDTH_PADDING) }];
		}),
	) as IFileDataSchemaProperties<T>;

	return { ...schema, properties };
}

const rawConfigurationSchema: IFileDataSchema<IConfigurationExportData> = {
	name: CONFIGURATION_SHEET,
	properties: {
		palletMaxRows: { type: 'number', required: true, fileKey: 'PalletMaxRows' },
		palletMaxLayers: { type: 'number', required: true, fileKey: 'PalletMaxLayers' },
		palletMaxHeight: { type: 'number', required: true, fileKey: 'PalletMaxHeight' },
		palletElementPadding: { type: 'number', required: true, fileKey: 'PalletElementPadding' },
		rotateMaxWeight: { type: 'number', required: true, fileKey: 'RotateMaxWeight' },
		rotateMaxLengthToHeightAspectRatio: { type: 'number', required: true, fileKey: 'RotateMaxLengthToHeightAspectRatio' },
		rotateMinRatioSingleElementOnly: { type: 'boolean', required: true, fileKey: 'RotateMinRatioSingleElementOnly' },
		allowStackingWithoutSupportingBase: { type: 'boolean', required: true, fileKey: 'AllowStackingWithoutSupportingBase' },
		balancingMethod: { type: 'string', required: true, fileKey: 'BalancingMethod' },

		sortElementLengthEnabled: { type: 'boolean', required: true, fileKey: 'SortElementLengthEnabled' },
		sortElementLengthOrder: { type: 'string', required: false, fileKey: 'SortElementLengthOrder' },
		sortElementLengthPriority: { type: 'number', required: false, fileKey: 'SortElementLengthPriority' },
		sortPositionWeightEnabled: { type: 'boolean', required: true, fileKey: 'SortPositionWeightEnabled' },
		sortPositionWeightOrder: { type: 'string', required: false, fileKey: 'SortPositionWeightOrder' },
		sortPositionWeightPriority: { type: 'number', required: false, fileKey: 'SortPositionWeightPriority' },
		sortBatchNumberEnabled: { type: 'boolean', required: true, fileKey: 'SortBatchNumberEnabled' },
		sortBatchNumberOrder: { type: 'string', required: false, fileKey: 'SortBatchNumberOrder' },
		sortBatchNumberPriority: { type: 'number', required: false, fileKey: 'SortBatchNumberPriority' },
		sortProductionSequenceNumberEnabled: { type: 'boolean', required: true, fileKey: 'SortProductionSequenceNumberEnabled' },
		sortProductionSequenceNumberOrder: { type: 'string', required: false, fileKey: 'SortProductionSequenceNumberOrder' },
		sortProductionSequenceNumberPriority: { type: 'number', required: false, fileKey: 'SortProductionSequenceNumberPriority' },

		poolPalletEnabled: { type: 'boolean', required: true, fileKey: 'PoolPalletEnabled' },
		poolPalletUnspecified: { type: 'boolean', required: false, fileKey: 'PoolPalletUnspecified' },
		poolPalletPriority: { type: 'number', required: false, fileKey: 'PoolPalletPriority' },
		poolBatchEnabled: { type: 'boolean', required: true, fileKey: 'PoolBatchEnabled' },
		poolBatchUnspecified: { type: 'boolean', required: false, fileKey: 'PoolBatchUnspecified' },
		poolBatchPriority: { type: 'number', required: false, fileKey: 'PoolBatchPriority' },
		poolOrderEnabled: { type: 'boolean', required: true, fileKey: 'PoolOrderEnabled' },
		poolOrderUnspecified: { type: 'boolean', required: false, fileKey: 'PoolOrderUnspecified' },
		poolOrderPriority: { type: 'number', required: false, fileKey: 'PoolOrderPriority' },
		poolPartitionEnabled: { type: 'boolean', required: true, fileKey: 'PoolPartitionEnabled' },
		poolPartitionUnspecified: { type: 'boolean', required: false, fileKey: 'PoolPartitionUnspecified' },
		poolPartitionPriority: { type: 'number', required: false, fileKey: 'PoolPartitionPriority' },
		poolPalletDimensionEnabled: { type: 'boolean', required: true, fileKey: 'PoolPalletDimensionEnabled' },
		poolPalletDimensionUnspecified: { type: 'boolean', required: false, fileKey: 'PoolPalletDimensionUnspecified' },
		poolPalletDimensionPriority: { type: 'number', required: false, fileKey: 'PoolPalletDimensionPriority' },
		poolPalletTypeEnabled: { type: 'boolean', required: true, fileKey: 'PoolPalletTypeEnabled' },
		poolPalletTypeUnspecified: { type: 'boolean', required: false, fileKey: 'PoolPalletTypeUnspecified' },
		poolPalletTypePriority: { type: 'number', required: false, fileKey: 'PoolPalletTypePriority' },
		poolGlassFittingEnabled: { type: 'boolean', required: true, fileKey: 'PoolGlassFittingEnabled' },
		poolGlassFittingUnspecified: { type: 'boolean', required: false, fileKey: 'PoolGlassFittingUnspecified' },
		poolGlassFittingPriority: { type: 'number', required: false, fileKey: 'PoolGlassFittingPriority' },
	},
};
const ConfigurationSchema = withHeaderWidths(rawConfigurationSchema);

const StackParamSchema: IFileDataSchema<IStackParamExportData> = withHeaderWidths<IStackParamExportData>({
	name: STACK_PARAM_SHEET,
	allowEmpty: true,
	properties: {
		toHeight: { type: 'number', required: true, fileKey: 'ToHeight' },
		maxHeightAfter: { type: 'number', required: true, fileKey: 'MaxHeightAfter' },
		maxWeightElement: { type: 'number', required: true, fileKey: 'MaxWeightElement' },
	},
});

const LayerParamSchema: IFileDataSchema<ILayerParamExportData> = withHeaderWidths<ILayerParamExportData>({
	name: LAYER_PARAM_SHEET,
	allowEmpty: true,
	properties: {
		layer: { type: 'number', required: true, fileKey: 'Layer' },
		maxHeightAfter: { type: 'number', required: true, fileKey: 'MaxHeightAfter' },
		maxWeightElement: { type: 'number', required: true, fileKey: 'MaxWeightElement' },
	},
});

const CombinationSchema: IFileDataSchema<ICombinationExportData> = withHeaderWidths<ICombinationExportData>({
	name: COMBINATION_SHEET,
	allowEmpty: true,
	properties: {
		type: { type: 'string', required: true, fileKey: 'Type', width: 30 },
		elementType: { type: 'string', required: true, fileKey: 'ElementType', width: 30 },
	},
});

interface SortFieldDef {
	enabledKey: keyof IConfigurationExportData;
	orderKey: keyof IConfigurationExportData;
	priorityKey: keyof IConfigurationExportData;
	strategySelf: ISort['type']['self'];
}

const SORT_FIELDS: SortFieldDef[] = [
	{ enabledKey: 'sortElementLengthEnabled', orderKey: 'sortElementLengthOrder', priorityKey: 'sortElementLengthPriority', strategySelf: '/palletising/configuration-sorting-strategies/elementLength' },
	{ enabledKey: 'sortPositionWeightEnabled', orderKey: 'sortPositionWeightOrder', priorityKey: 'sortPositionWeightPriority', strategySelf: '/palletising/configuration-sorting-strategies/positionWeight' },
	{ enabledKey: 'sortBatchNumberEnabled', orderKey: 'sortBatchNumberOrder', priorityKey: 'sortBatchNumberPriority', strategySelf: '/palletising/configuration-sorting-strategies/batchNumber' },
	{ enabledKey: 'sortProductionSequenceNumberEnabled', orderKey: 'sortProductionSequenceNumberOrder', priorityKey: 'sortProductionSequenceNumberPriority', strategySelf: '/palletising/configuration-sorting-strategies/productionSequenceNumber' },
];

interface PoolFieldDef {
	enabledKey: keyof IConfigurationExportData;
	unspecifiedKey: keyof IConfigurationExportData;
	priorityKey: keyof IConfigurationExportData;
	strategySelf: IPool['type']['self'];
}

const POOL_FIELDS: PoolFieldDef[] = [
	{ enabledKey: 'poolPalletEnabled', unspecifiedKey: 'poolPalletUnspecified', priorityKey: 'poolPalletPriority', strategySelf: '/palletising/configuration-pooling-strategies/pallet' },
	{ enabledKey: 'poolBatchEnabled', unspecifiedKey: 'poolBatchUnspecified', priorityKey: 'poolBatchPriority', strategySelf: '/palletising/configuration-pooling-strategies/batch' },
	{ enabledKey: 'poolOrderEnabled', unspecifiedKey: 'poolOrderUnspecified', priorityKey: 'poolOrderPriority', strategySelf: '/palletising/configuration-pooling-strategies/order' },
	{ enabledKey: 'poolPartitionEnabled', unspecifiedKey: 'poolPartitionUnspecified', priorityKey: 'poolPartitionPriority', strategySelf: '/palletising/configuration-pooling-strategies/partition' },
	{ enabledKey: 'poolPalletDimensionEnabled', unspecifiedKey: 'poolPalletDimensionUnspecified', priorityKey: 'poolPalletDimensionPriority', strategySelf: '/palletising/configuration-pooling-strategies/palletdimension' },
	{ enabledKey: 'poolPalletTypeEnabled', unspecifiedKey: 'poolPalletTypeUnspecified', priorityKey: 'poolPalletTypePriority', strategySelf: '/palletising/configuration-pooling-strategies/pallettype' },
	{ enabledKey: 'poolGlassFittingEnabled', unspecifiedKey: 'poolGlassFittingUnspecified', priorityKey: 'poolGlassFittingPriority', strategySelf: '/palletising/configuration-pooling-strategies/glassfitting' },
];

function orderToLabel(order: 0 | 1): OrderLabel {
	return order === 0 ? 'Ascending' : 'Descending';
}

function labelToOrder(label: OrderLabel): 0 | 1 {
	return label === 'Ascending' ? 0 : 1;
}

function normalizeName(name: unknown): string {
	return name === null || name === undefined ? '' : String(name).trim().toLowerCase();
}

@Injectable({
	providedIn: 'root',
})
export class PalletisingConfigTransferService implements ITransferService<IConfiguration> {
	private translationPrefix = 'masterdata.palletising.configurationImportExport';

	constructor(
		private crudService: CrudService,
		private palletisingService: PalletisingService,
		private excelFileTransfer: ExcelFileTransfer<any>,
		private translator: TranslateService,
	) {}

	public hasTemplate = () => true;
	public getTemplate = (): void => {
		this.downloadTemplate();
	};

	// There is exactly one configuration per account, so - like carting-config - export always
	// carries a single settings row, plus the three list-shaped sub-tables the settings row can't
	// hold (stack params, layer params, pallet type combinations), one sheet each in the same file -
	// built via ExcelFileTransfer.exportWorkbook, which parses/writes the whole workbook in one pass.
	public exportData(configs: IConfiguration[]): Observable<IConfiguration[]> {
		const config = configs[0];
		if (!config) {
			return throwError(() => new Error(this.translator.instant(`${this.translationPrefix}.export.errors.missing-configuration`)));
		}

		// catchError is placed directly on fetchPalletTypes(), before mergeMap, so it can't also catch
		// (and mislabel as a fetch failure) an error thrown inside the mergeMap callback below.
		return this.palletisingService.fetchPalletTypes().pipe(
			catchError((error) => throwError(() => new Error(this.translator.instant(`${this.translationPrefix}.export.errors.fetch-pallet-types-failed`, { error: error.message })))),
			mergeMap((roster) => {
				const nameBySelf = new Map(roster.items.map((item) => [item.self, item.name]));

				const { ambiguousNames, hasMissingReference } = this.findCombinationExportProblems(config, roster.items, nameBySelf);
				if (ambiguousNames.length > 0) {
					return throwError(() => new Error(this.translator.instant(`${this.translationPrefix}.export.errors.ambiguous-pallet-type-names`, { names: ambiguousNames.join(', ') })));
				}
				if (hasMissingReference) {
					return throwError(() => new Error(this.translator.instant(`${this.translationPrefix}.export.errors.missing-pallet-type-reference`)));
				}

				const sheets = [
					{ schema: ConfigurationSchema, data: [this.toConfigurationRow(config)] },
					{ schema: StackParamSchema, data: this.toStackParamRows(config) },
					{ schema: LayerParamSchema, data: this.toLayerParamRows(config) },
					{ schema: CombinationSchema, data: this.toCombinationRows(config, nameBySelf) },
				];

				// ExcelFileTransfer writes the file (and validates cell lengths) synchronously before
				// returning its observable, so calling it directly here would let a real write failure
				// throw past catchError below instead of reaching it - defer() delays that call until
				// subscription, inside RxJS's own error handling.
				return defer(() => this.excelFileTransfer.exportWorkbook(fileName, sheets)).pipe(
					map(() => configs),
					catchError((error) => {
						throw new Error(this.translator.instant(`${this.translationPrefix}.export.errors.export-failed`, { error: error.message }));
					}),
				);
			}),
		);
	}

	// Scoped to only the pallet types this config's combinations actually reference, not the whole
	// roster - a duplicate name elsewhere that this config never points at isn't this export's problem.
	private findCombinationExportProblems(config: IConfiguration, roster: IBase[], nameBySelf: Map<string, string>): { ambiguousNames: string[]; hasMissingReference: boolean } {
		const selfsByNormalizedName = new Map<string, string[]>();
		for (const item of roster) {
			const key = normalizeName(item.name);
			const list = selfsByNormalizedName.get(key);
			if (list) list.push(item.self);
			else selfsByNormalizedName.set(key, [item.self]);
		}

		const referencedSelfs = new Set<string>();
		for (const combination of config.palletTypeCombinations) {
			referencedSelfs.add(combination.palletType.self);
			for (const element of combination.elementPalletTypes) {
				referencedSelfs.add(element.palletType.self);
			}
		}

		const ambiguousNames = new Set<string>();
		let hasMissingReference = false;
		for (const self of referencedSelfs) {
			const name = nameBySelf.get(self);
			if (!name) {
				hasMissingReference = true;
				continue;
			}
			const matches = selfsByNormalizedName.get(normalizeName(name));
			if (matches && matches.length > 1) {
				ambiguousNames.add(name);
			}
		}

		return { ambiguousNames: Array.from(ambiguousNames), hasMissingReference };
	}

	public downloadTemplate(): void {
		const exampleConfiguration: IConfigurationExportData = {
			palletMaxRows: 10,
			palletMaxLayers: 5,
			palletMaxHeight: 2000,
			palletElementPadding: 10,
			rotateMaxWeight: 500,
			rotateMaxLengthToHeightAspectRatio: 1.5,
			rotateMinRatioSingleElementOnly: true,
			allowStackingWithoutSupportingBase: true,
			balancingMethod: 'Uniform',
			sortElementLengthEnabled: true,
			sortElementLengthOrder: 'Ascending',
			sortElementLengthPriority: 1,
			sortPositionWeightEnabled: false,
			sortBatchNumberEnabled: false,
			sortProductionSequenceNumberEnabled: false,
			poolPalletEnabled: true,
			poolPalletUnspecified: true,
			poolPalletPriority: 1,
			poolBatchEnabled: false,
			poolOrderEnabled: false,
			poolPartitionEnabled: false,
			poolPalletDimensionEnabled: false,
			poolPalletTypeEnabled: false,
			poolGlassFittingEnabled: false,
		};
		const exampleStackParams: IStackParamExportData[] = [
			{ toHeight: 1000, maxHeightAfter: 500, maxWeightElement: 100 },
			{ toHeight: 2000, maxHeightAfter: 1000, maxWeightElement: 200 },
		];
		const exampleLayerParams: ILayerParamExportData[] = [
			{ layer: 1, maxHeightAfter: 500, maxWeightElement: 100 },
			{ layer: 2, maxHeightAfter: 1000, maxWeightElement: 200 },
		];
		const exampleCombinations: ICombinationExportData[] = [
			{ type: 'Example Type', elementType: 'Example Element Type' },
			{ type: 'Example Type', elementType: 'Example Element Type 2' },
			{ type: 'Example Type 2', elementType: 'Example Element Type' },
		];

		this.excelFileTransfer
			.exportWorkbook(`${fileName}Template`, [
				{ schema: ConfigurationSchema, data: [exampleConfiguration] },
				{ schema: StackParamSchema, data: exampleStackParams },
				{ schema: LayerParamSchema, data: exampleLayerParams },
				{ schema: CombinationSchema, data: exampleCombinations },
			])
			.subscribe();
	}

	private toConfigurationRow(config: IConfiguration): IConfigurationExportData {
		const row: IConfigurationExportData = {
			palletMaxRows: config.palletMaxRows,
			palletMaxLayers: config.palletMaxLayers,
			palletMaxHeight: config.palletMaxHeight,
			palletElementPadding: config.palletElementPadding,
			rotateMaxWeight: config.rotateMaxWeight,
			rotateMaxLengthToHeightAspectRatio: config.rotateMaxLengthToHeightAspectRatio,
			rotateMinRatioSingleElementOnly: config.rotateMinRatioSingleElementOnly,
			allowStackingWithoutSupportingBase: config.allowStackingWithoutSupportingBase,
			balancingMethod: this.balanceMethodToLabel(config.balancing[0]),
			sortElementLengthEnabled: false,
			sortPositionWeightEnabled: false,
			sortBatchNumberEnabled: false,
			sortProductionSequenceNumberEnabled: false,
			poolPalletEnabled: false,
			poolBatchEnabled: false,
			poolOrderEnabled: false,
			poolPartitionEnabled: false,
			poolPalletDimensionEnabled: false,
			poolPalletTypeEnabled: false,
			poolGlassFittingEnabled: false,
		};

		for (const def of SORT_FIELDS) {
			const sort = config.sorting.find((entry) => entry.type.self === def.strategySelf);
			row[def.enabledKey] = !!sort as never;
			if (sort) {
				row[def.orderKey] = orderToLabel(sort.order) as never;
				row[def.priorityKey] = sort.priority as never;
			}
		}

		for (const def of POOL_FIELDS) {
			const pool = config.pooling.find((entry) => entry.type.self === def.strategySelf);
			row[def.enabledKey] = !!pool as never;
			if (pool) {
				row[def.unspecifiedKey] = pool.isolateNullValues as never;
				row[def.priorityKey] = pool.priority as never;
			}
		}

		return row;
	}

	private balanceMethodToLabel(balance: IBalance | undefined): BalanceLabel {
		if (!balance) return 'None';
		return BALANCE_LABELS[balance.method + 1];
	}

	private toStackParamRows(config: IConfiguration): IStackParamExportData[] {
		return config.stacking.filter((param): param is IStackParam => param.type.self === STACK_TYPE_SELF && 'toHeight' in param).map(({ toHeight, maxHeightAfter, maxWeightElement }) => ({ toHeight, maxHeightAfter, maxWeightElement }));
	}

	private toLayerParamRows(config: IConfiguration): ILayerParamExportData[] {
		return config.stacking.filter((param): param is ILayerParam => param.type.self === LAYER_TYPE_SELF && 'layer' in param).map(({ layer, maxHeightAfter, maxWeightElement }) => ({ layer, maxHeightAfter, maxWeightElement }));
	}

	private toCombinationRows(config: IConfiguration, nameBySelf: Map<string, string>): ICombinationExportData[] {
		const rows: ICombinationExportData[] = [];
		for (const combination of config.palletTypeCombinations) {
			const typeName = nameBySelf.get(combination.palletType.self);
			if (!typeName) continue;

			for (const element of combination.elementPalletTypes) {
				const elementName = nameBySelf.get(element.palletType.self);
				if (!elementName) continue;
				rows.push({ type: typeName, elementType: elementName });
			}
		}
		return rows;
	}

	// The whole file resolves to exactly one validation-or-save outcome instead of a per-row list,
	// like carting-config: there is nothing to batch when the entity is a singleton. Every sheet's
	// errors are collected together before anything is saved, so a mistake on one sheet can never
	// let another sheet's change through half-applied.
	public importData(file: File | File[]): Observable<ImportResponse> {
		const ImportErrors: ImportErrorTypes[] = [];
		const files = Array.isArray(file) ? file : [file];

		return readFiles(files, 'binary').pipe(
			mergeMap((buffer) => {
				// One buffer parse, all four sheets extracted from it via ExcelFileTransfer.importWorkbook -
				// each sheet's own parse errors (missing sheet, bad header, etc.) surface on its own entry
				// rather than aborting the whole read.
				let results: Array<{ schema: IFileDataSchema<any>; rows: Array<IImportedData<any>>; errors: xlsxImportError[] }>;
				try {
					results = this.excelFileTransfer.importWorkbook(buffer, [ConfigurationSchema, StackParamSchema, LayerParamSchema, CombinationSchema]);
				} catch (error) {
					ImportErrors.push({ status: ImportStatus.ParsingError, message: error.message } as FailedParse);
					return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
				}
				const [configResult, stackResult, layerResult, combinationResult] = results;

				for (const result of [configResult, stackResult, layerResult, combinationResult]) {
					result.errors.forEach((e: xlsxImportError) => {
						ImportErrors.push({ message: e.error, status: ImportStatus.ParsingError } as FailedParse);
					});
				}

				if (ImportErrors.length > 0) {
					return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
				}

				const configRows: Array<IImportedData<IConfigurationExportData>> = configResult.rows;
				if (configRows.length !== 1) {
					ImportErrors.push({
						status: ImportStatus.ParsingError,
						message: this.translator.instant(configRows.length === 0 ? `${this.translationPrefix}.import.errors.missing-configuration-row` : `${this.translationPrefix}.import.errors.too-many-configuration-rows`),
					} as FailedParse);
					return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
				}

				const { data: configRow, location } = configRows[0];
				const errors: Record<string, ValidationErrors> = {};

				this.validateConfigurationRow(configRow, errors);
				const sorting = this.parseSorting(configRow, errors);
				const pooling = this.parsePooling(configRow, errors);

				if (Object.keys(errors).length > 0) {
					ImportErrors.push({ Id: configId, location, status: ImportStatus.ValidationError, errors } as FailedValidationImport);
				}

				const stackParams = this.validateStackParams(stackResult.rows, ImportErrors);
				const layerParams = this.validateLayerParams(layerResult.rows, ImportErrors);

				// Mirrors carting-config's own gating of its single network dependency behind
				// already-known errors: a bad row anywhere means the file can never be saved regardless
				// of what the roster fetch below would find, so skip it rather than spend a network
				// round trip - and risk a transient failure there injecting an unrelated
				// fetch-pallet-types-failed message alongside the real, already-known problem - on an
				// import that was already going to be rejected.
				if (ImportErrors.length > 0) {
					return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
				}

				return this.palletisingService.fetchPalletTypes().pipe(
					mergeMap((roster) => {
						const combinations = this.validateCombinations(combinationResult.rows, roster.items, ImportErrors);

						if (ImportErrors.length > 0) {
							return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
						}

						return this.palletisingService.fetchConfigurations().pipe(
							mergeMap((response) => {
								const existing = response.items[0];
								if (!existing) {
									ImportErrors.push({
										status: ImportStatus.ParsingError,
										message: this.translator.instant(`${this.translationPrefix}.import.errors.missing-existing-configuration`),
									} as FailedParse);
									return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
								}

								const payload = this.buildConfiguration(existing.self, configRow, sorting, pooling, stackParams, layerParams, combinations);

								return this.crudService.update<IConfiguration>(payload).pipe(
									map(() => ({ successfulImports: [{ Id: configId, status: ImportStatus.SuccessUpdate } as SuccessFullImport], errors: ImportErrors }) as ImportResponse),
									catchError((error) => {
										ImportErrors.push({
											Id: configId,
											location,
											status: ImportStatus.Failed,
											message: this.translator.instant(`${this.translationPrefix}.import.errors.update-failed${this.saveFailedErrorKeySuffix(error)}`),
										} as FailedImport);
										return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
									}),
								);
							}),
							// Mirrors the update() catchError just above: without this, a failure fetching the
							// existing configuration (network drop, transient 5xx) would escape the
							// ImportResponse contract every other failure path here follows.
							catchError((error) => {
								console.error(error);
								ImportErrors.push({
									status: ImportStatus.ParsingError,
									message: this.translator.instant(`${this.translationPrefix}.import.errors.fetch-configuration-failed`),
								} as FailedParse);
								return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
							}),
						);
					}),
					catchError((error) => {
						console.error(error);
						ImportErrors.push({
							status: ImportStatus.ParsingError,
							message: this.translator.instant(`${this.translationPrefix}.import.errors.fetch-pallet-types-failed`),
						} as FailedParse);
						return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
					}),
				);
			}),
		);
	}

	private validateConfigurationRow(row: IConfigurationExportData, errors: Record<string, ValidationErrors>): void {
		this.validateRange(errors, 'palletMaxRows', row.palletMaxRows, 0, SCALAR_RANGE_MAX, true);
		this.validateRange(errors, 'palletMaxLayers', row.palletMaxLayers, 0, SCALAR_RANGE_MAX, true);
		this.validateRange(errors, 'palletMaxHeight', row.palletMaxHeight, 0, SCALAR_RANGE_MAX, true);
		this.validateRange(errors, 'palletElementPadding', row.palletElementPadding, 0, SCALAR_RANGE_MAX, true);
		this.validateRange(errors, 'rotateMaxWeight', row.rotateMaxWeight, 0, SCALAR_RANGE_MAX, true);
		this.validateRange(errors, 'rotateMaxLengthToHeightAspectRatio', row.rotateMaxLengthToHeightAspectRatio, 0, SCALAR_RANGE_MAX, false);

		const balanceLabel = (row.balancingMethod ?? '').trim();
		const matchedBalance = BALANCE_LABELS.find((label) => label.toLowerCase() === balanceLabel.toLowerCase());
		if (!matchedBalance) {
			errors['balancingMethod'] = { invalid: this.translator.instant(`${this.translationPrefix}.import.errors.invalid-balancing-method`, { value: row.balancingMethod }) };
		}
	}

	// Uniqueness and the no-lower-bound rule are scoped to the list passed in, mirroring the live
	// form (see palletising.configuration.view.model.ts's notUnique()): sorting priorities may only
	// collide with other sorting priorities, never with pooling's, and vice versa. A falsy priority
	// (0) is exempt from uniqueness, matching notUnique()'s own `if (!target.value) return null` -
	// an enabled-but-never-prioritized strategy is persisted as priority 0 by getSortingFromViewModel()'s
	// `priority ? Number(priority) : 0`, so multiple strategies left at that default must import
	// back cleanly, not collide.
	private parseSorting(row: IConfigurationExportData, errors: Record<string, ValidationErrors>): ISort[] {
		const seenPriorities = new Set<number>();
		const result: ISort[] = [];

		for (const def of SORT_FIELDS) {
			if (!row[def.enabledKey]) continue;

			const rawOrder = row[def.orderKey] as string | undefined;
			let order: 0 | 1 = 1;
			if (rawOrder !== undefined && String(rawOrder).trim() !== '') {
				const normalized = ORDER_LABELS.find((label) => label.toLowerCase() === String(rawOrder).trim().toLowerCase());
				if (!normalized) {
					errors[def.orderKey] = { invalid: this.translator.instant(`${this.translationPrefix}.import.errors.invalid-sort-order`, { value: rawOrder }) };
					continue;
				}
				order = labelToOrder(normalized);
			}

			const priority = this.parsePriority(row[def.priorityKey] as number | undefined, def.priorityKey, errors, seenPriorities);
			if (priority === undefined) continue;

			result.push({ order, priority, type: { self: def.strategySelf } });
		}

		return result;
	}

	private parsePooling(row: IConfigurationExportData, errors: Record<string, ValidationErrors>): IPool[] {
		const seenPriorities = new Set<number>();
		const result: IPool[] = [];

		for (const def of POOL_FIELDS) {
			if (!row[def.enabledKey]) continue;

			const priority = this.parsePriority(row[def.priorityKey] as number | undefined, def.priorityKey, errors, seenPriorities);
			if (priority === undefined) continue;

			result.push({
				isolateNullValues: !!row[def.unspecifiedKey],
				priority,
				type: { self: def.strategySelf },
			});
		}

		return result;
	}

	private parsePriority(rawPriority: number | undefined, key: string, errors: Record<string, ValidationErrors>, seenPriorities: Set<number>): number | undefined {
		if (rawPriority === undefined || rawPriority === null) {
			return 0;
		}

		// No integer check, matching the live form's own priorityValidators (notUnique + Validators.max
		// only - see palletising.configuration.view.model.ts): a priority set to a non-integer value
		// through some other path must still round-trip through export/import unchanged.
		if (!Number.isFinite(rawPriority) || rawPriority > PRIORITY_MAX) {
			errors[key] = { range: this.translator.instant(`${this.translationPrefix}.import.errors.invalid-priority`) };
			return undefined;
		}

		if (rawPriority !== 0) {
			if (seenPriorities.has(rawPriority)) {
				errors[key] = { duplicate: this.translator.instant(`${this.translationPrefix}.import.errors.duplicate-priority`) };
				return undefined;
			}
			seenPriorities.add(rawPriority);
		}

		return rawPriority;
	}

	private validateRange(errors: Record<string, ValidationErrors>, field: string, value: number, min: number, max: number, integer: boolean): void {
		if (value === undefined || value === null || !Number.isFinite(value) || value < min || value > max || (integer && !Number.isInteger(value))) {
			errors[field] = { range: this.translator.instant(`${this.translationPrefix}.import.errors.invalid-range`, { field, min, max }) };
		}
	}

	private validateStackParams(rows: Array<IImportedData<IStackParamExportData>>, ImportErrors: ImportErrorTypes[]): IStackParam[] {
		const result: IStackParam[] = [];
		for (const { data, location } of rows) {
			const errors: Record<string, ValidationErrors> = {};
			this.validateRange(errors, 'toHeight', data.toHeight, STACKING_VALUE_MIN, STACKING_VALUE_MAX, true);
			this.validateRange(errors, 'maxHeightAfter', data.maxHeightAfter, STACKING_VALUE_MIN, STACKING_VALUE_MAX, true);
			this.validateRange(errors, 'maxWeightElement', data.maxWeightElement, STACKING_VALUE_MIN, STACKING_VALUE_MAX, true);

			if (Object.keys(errors).length > 0) {
				ImportErrors.push({ Id: location ?? '', location, status: ImportStatus.ValidationError, errors } as FailedValidationImport);
				continue;
			}
			result.push({ toHeight: data.toHeight, maxHeightAfter: data.maxHeightAfter, maxWeightElement: data.maxWeightElement, type: { self: STACK_TYPE_SELF } });
		}
		return result;
	}

	private validateLayerParams(rows: Array<IImportedData<ILayerParamExportData>>, ImportErrors: ImportErrorTypes[]): ILayerParam[] {
		const result: ILayerParam[] = [];
		for (const { data, location } of rows) {
			const errors: Record<string, ValidationErrors> = {};
			this.validateRange(errors, 'layer', data.layer, STACKING_VALUE_MIN, STACKING_VALUE_MAX, true);
			this.validateRange(errors, 'maxHeightAfter', data.maxHeightAfter, STACKING_VALUE_MIN, STACKING_VALUE_MAX, true);
			this.validateRange(errors, 'maxWeightElement', data.maxWeightElement, STACKING_VALUE_MIN, STACKING_VALUE_MAX, true);

			if (Object.keys(errors).length > 0) {
				ImportErrors.push({ Id: location ?? '', location, status: ImportStatus.ValidationError, errors } as FailedValidationImport);
				continue;
			}
			result.push({ layer: data.layer, maxHeightAfter: data.maxHeightAfter, maxWeightElement: data.maxWeightElement, type: { self: LAYER_TYPE_SELF } });
		}
		return result;
	}

	// A (type, elementType) pair is the combination sheet's real identity - JSON-encoded rather than
	// joined with a plain separator as cheap insurance against a delimiter collision, matching
	// grouping.ts's rosterKey() convention.
	private validateCombinations(rows: Array<IImportedData<ICombinationExportData>>, roster: IBase[], ImportErrors: ImportErrorTypes[]): IPalletTypeCombination[] {
		// Grouped by normalized name rather than a straight name -> self Map: nothing enforces pallet
		// type/dimension name uniqueness server-side, so two roster entries could normalize to the
		// same key. A plain Map would silently keep whichever entry came last and resolve every row
		// naming that collided name to the WRONG self - resolveByName below rejects that name as
		// ambiguous instead.
		const selfsByName = new Map<string, string[]>();
		for (const item of roster) {
			const key = normalizeName(item.name);
			const list = selfsByName.get(key);
			if (list) list.push(item.self);
			else selfsByName.set(key, [item.self]);
		}
		const resolveByName = (name: string): string | undefined => {
			const matches = selfsByName.get(normalizeName(name));
			return matches?.length === 1 ? matches[0] : undefined;
		};
		const isAmbiguous = (name: string): boolean => (selfsByName.get(normalizeName(name))?.length ?? 0) > 1;

		const seenPairs = new Set<string>();
		const elementsByType = new Map<string, IPalletType[]>();
		const typeSelfOrder: string[] = [];

		for (const { data, location } of rows) {
			const errors: Record<string, ValidationErrors> = {};
			const typeSelf = resolveByName(data.type);
			const elementSelf = resolveByName(data.elementType);

			if (!typeSelf) {
				errors['type'] = {
					[isAmbiguous(data.type) ? 'ambiguous' : 'unknown']: this.translator.instant(`${this.translationPrefix}.import.errors.${isAmbiguous(data.type) ? 'ambiguous-pallet-type' : 'unknown-pallet-type'}`, { value: data.type }),
				};
			}
			if (!elementSelf) {
				errors['elementType'] = {
					[isAmbiguous(data.elementType) ? 'ambiguous' : 'unknown']: this.translator.instant(`${this.translationPrefix}.import.errors.${isAmbiguous(data.elementType) ? 'ambiguous-pallet-type' : 'unknown-pallet-type'}`, { value: data.elementType }),
				};
			}
			if (typeSelf && elementSelf && typeSelf === elementSelf) {
				errors['elementType'] = { self: this.translator.instant(`${this.translationPrefix}.import.errors.combination-self-reference`, { value: data.type }) };
			}

			if (Object.keys(errors).length > 0) {
				ImportErrors.push({ Id: data.type || data.elementType || '', location, status: ImportStatus.ValidationError, errors } as FailedValidationImport);
				continue;
			}

			const pairKey = JSON.stringify([typeSelf, elementSelf]);
			if (seenPairs.has(pairKey)) {
				ImportErrors.push({
					Id: data.type,
					location,
					status: ImportStatus.ValidationError,
					errors: { type: { duplicate: this.translator.instant(`${this.translationPrefix}.import.errors.duplicate-combination`, { type: data.type, elementType: data.elementType }) } },
				} as FailedValidationImport);
				continue;
			}
			seenPairs.add(pairKey);

			if (!elementsByType.has(typeSelf)) {
				elementsByType.set(typeSelf, []);
				typeSelfOrder.push(typeSelf);
			}
			elementsByType.get(typeSelf).push({ palletType: { self: elementSelf as never } });
		}

		return typeSelfOrder.map((typeSelf) => ({
			palletType: { self: typeSelf as never },
			elementPalletTypes: elementsByType.get(typeSelf),
		}));
	}

	private buildConfiguration(self: string, row: IConfigurationExportData, sorting: ISort[], pooling: IPool[], stackParams: IStackParam[], layerParams: ILayerParam[], combinations: IPalletTypeCombination[]): IConfiguration {
		const balanceLabel = BALANCE_LABELS.find((label) => label.toLowerCase() === (row.balancingMethod ?? '').trim().toLowerCase());
		const balancing: IBalance[] = balanceLabel && balanceLabel !== 'None' ? [{ method: (BALANCE_LABELS.indexOf(balanceLabel) - 1) as TBalanceMethod, type: { self: '/palletising/configuration-balancing-strategies/weight' } }] : [];

		return {
			self,
			allowStackingWithoutSupportingBase: row.allowStackingWithoutSupportingBase,
			balancing,
			palletElementPadding: row.palletElementPadding,
			palletMaxHeight: row.palletMaxHeight,
			palletMaxLayers: row.palletMaxLayers,
			palletMaxRows: row.palletMaxRows,
			palletTypeCombinations: combinations,
			pooling,
			rotateMaxLengthToHeightAspectRatio: row.rotateMaxLengthToHeightAspectRatio,
			rotateMaxWeight: row.rotateMaxWeight,
			rotateMinRatioSingleElementOnly: row.rotateMinRatioSingleElementOnly,
			sorting,
			stacking: [...stackParams, ...layerParams],
		};
	}

	private saveFailedErrorKeySuffix(error: unknown): string {
		const status = (error as HttpErrorResponse)?.status;
		if (status === 400) return '-bad-request';
		if (status === 404) return '-not-found';
		return '';
	}
}
